import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiX, 
  FiSave, 
  FiMail, 
  FiPhone, 
  FiUsers, 
  FiClock, 
  FiLoader 
} = FiIcons;

const EditRegistrationModal = ({ registration, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    email: '',
    phone: '',
    contactMethod: 'email',
    persons: 1,
    time: '12:00',
    preference: 'suppe',
    total_price: 0
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialisiere Formulardaten mit den Registrierungsdaten
  useEffect(() => {
    if (registration) {
      setFormData({
        id: registration.id || '',
        name: registration.name || '',
        email: registration.email || '',
        phone: registration.phone || '',
        contactMethod: registration.contact_method || (registration.email ? 'email' : 'phone'),
        persons: registration.persons || 1,
        time: registration.time || '12:00',
        preference: registration.preference || 'suppe',
        total_price: registration.total_price || 0,
        day: registration.day || '',
        menu_name: registration.menu_name || ''
      });
    }
  }, [registration]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }
    
    if (formData.contactMethod === 'email') {
      if (!formData.email.trim()) {
        newErrors.email = 'E-Mail ist erforderlich';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Ungültige E-Mail-Adresse';
      }
    } else {
      if (!formData.phone.trim()) {
        newErrors.phone = 'Telefonnummer ist erforderlich';
      } else if (!/^\+?[\d\s\-()]+$/.test(formData.phone)) {
        newErrors.phone = 'Ungültige Telefonnummer';
      }
    }
    
    if (formData.persons < 1 || formData.persons > 20) {
      newErrors.persons = 'Anzahl Personen muss zwischen 1 und 20 liegen';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Berechne den neuen Gesamtpreis basierend auf der Personenzahl
      // Da wir den Preis pro Person nicht kennen, verwenden wir den vorhandenen Gesamtpreis und berechnen den Preis pro Person
      const pricePerPerson = registration.total_price / registration.persons;
      const newTotalPrice = pricePerPerson * formData.persons;
      
      const updatedRegistration = {
        ...formData,
        total_price: newTotalPrice
      };
      
      await onSave(updatedRegistration);
    } catch (error) {
      console.error('Fehler beim Speichern der Änderungen:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: name === 'persons' ? parseInt(value) || 1 : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-xl p-6 max-w-md w-full"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800">
            Anmeldung bearbeiten
          </h2>
          <button
            onClick={onCancel}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Menüinformationen (nicht editierbar) */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="text-sm font-medium text-gray-700 mb-1">
              {formData.day}
            </div>
            <div className="text-sm text-gray-600">
              {formData.menu_name}
            </div>
          </div>
          
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Name des Kunden"
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>
          
          {/* Contact Method */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Kontakt-Methode *
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, contactMethod: 'email' }))}
                className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-xl border transition-colors ${
                  formData.contactMethod === 'email'
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={FiMail} />
                <span>E-Mail</span>
              </button>
              
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, contactMethod: 'phone' }))}
                className={`flex items-center justify-center space-x-2 py-3 px-4 rounded-xl border transition-colors ${
                  formData.contactMethod === 'phone'
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                <SafeIcon icon={FiPhone} />
                <span>Telefon</span>
              </button>
            </div>
          </div>
          
          {/* Email or Phone */}
          {formData.contactMethod === 'email' ? (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                E-Mail-Adresse *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="kunde@example.com"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Telefonnummer *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="+41 79 123 45 67"
              />
              {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
            </div>
          )}
          
          {/* Number of Persons */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Anzahl Personen *
            </label>
            <div className="relative">
              <SafeIcon icon={FiUsers} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="number"
                name="persons"
                value={formData.persons}
                onChange={handleInputChange}
                min="1"
                max="20"
                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.persons ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {errors.persons && <p className="text-red-500 text-sm mt-1">{errors.persons}</p>}
          </div>
          
          {/* Time */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Gewünschte Zeit
            </label>
            <div className="relative">
              <SafeIcon icon={FiClock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Standard: 12:00 Uhr</p>
          </div>
          
          {/* Soup or Salad */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Vorspeise
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, preference: 'suppe' }))}
                className={`py-3 px-4 rounded-xl border transition-colors ${
                  formData.preference === 'suppe'
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                Suppe
              </button>
              
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, preference: 'salat' }))}
                className={`py-3 px-4 rounded-xl border transition-colors ${
                  formData.preference === 'salat'
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
                }`}
              >
                Salat
              </button>
            </div>
          </div>
          
          {/* Buttons */}
          <div className="flex space-x-4 pt-2">
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`flex-1 flex items-center justify-center space-x-2 bg-primary-500 text-white py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors duration-300 ${
                isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <SafeIcon icon={FiLoader} className="animate-spin" />
                  <span>Wird gespeichert...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} />
                  <span>Speichern</span>
                </>
              )}
            </motion.button>
            
            <motion.button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex-1 flex items-center justify-center space-x-2 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors duration-300"
            >
              <SafeIcon icon={FiX} />
              <span>Abbrechen</span>
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditRegistrationModal;